"use client";

import { getFormProps, getInputProps, useForm } from "@conform-to/react";
import { parseWithZod } from "@conform-to/zod/v4";
import { useActionState } from "react";

import {
  FORM_VALIDATE_OPTIONS,
  formConstraint,
} from "@/components/forms/conform-options";
import { DocumentCheckbox } from "@/components/forms/document-checkbox";
import { FieldError, FormError } from "@/components/forms/form-error";
import { LabelRow } from "@/components/forms/label-row";
import { SubmitButton } from "@/components/forms/submit-button";
import { Input } from "@/components/ui/input";
import { REFERRAL_SOURCE_OTHER } from "@/lib/onboarding/constants";
import { onboardingSchema } from "@/lib/onboarding/schema";

import { submitOnboardingAction } from "../../actions";
import type { StepProps } from "../types";
import { StepFooter, StepHeader } from "../ui";

const constraint = formConstraint(onboardingSchema);

/** Fields answered on earlier steps; they ride along as hidden inputs. A
 *  server error on one of them can't be shown beside an input the user can
 *  see, so it's surfaced at the top instead. */
const CARRIED_ANSWERS = [
  "audience",
  "track",
  "exam_timeframe",
  "concerns",
  "weekly_scenario_goal",
  "referral_source",
] as const;
const CARRIED_FIELDS = [...CARRIED_ANSWERS, "tos_version", "privacy_version"] as const;

/** The one submit. Personal fields are asked HERE, beside the consent
 *  checkboxes, and go to `record_onboarding` in the same transaction as the
 *  legal acceptance and every earlier answer, so nothing personal is ever
 *  stored before the user agrees to the privacy policy. */
export function AboutStep({ state, env }: StepProps) {
  const { answers } = state;
  const instructor = answers.audience === "instructor";
  const [lastResult, action] = useActionState(submitOnboardingAction, undefined);
  const [form, fields] = useForm({
    lastResult,
    constraint,
    // Seed through Conform, not an input defaultValue: after a server error
    // reply re-seeds the form, a name the user edited must survive instead of
    // reverting to the provider's name.
    defaultValue: { display_name: env.initialDisplayName },
    ...FORM_VALIDATE_OPTIONS,
    onValidate({ formData }) {
      return parseWithZod(formData, { schema: onboardingSchema });
    },
  });

  const carriedError = CARRIED_FIELDS.map((f) => fields[f].errors?.[0]).find(Boolean);

  return (
    <form
      {...getFormProps(form)}
      action={action}
      noValidate
      className="flex flex-1 flex-col"
    >
      {env.next ? <input type="hidden" name="next" value={env.next} /> : null}
      {CARRIED_ANSWERS.flatMap((name) =>
        [answers[name] ?? []].flat().filter((v) => v !== "").map((value) => (
          <input key={`${name}:${value}`} type="hidden" name={name} value={String(value)} />
        )),
      )}
      <input type="hidden" name="tos_version" value={env.tosVersion} />
      <input type="hidden" name="privacy_version" value={env.privacyVersion} />

      <StepHeader kicker="Finish setup" title="About you" />

      <div className="mt-7 space-y-5">
        <FormError id={form.errorId} message={form.errors?.[0] ?? carriedError} />

        <div className="space-y-1.5">
          <LabelRow htmlFor={fields.display_name.id}>Full name</LabelRow>
          <Input
            {...getInputProps(fields.display_name, { type: "text" })}
            placeholder="Sarah Jones"
            autoComplete="name"
            className="h-12 text-base"
          />
          <FieldError id={fields.display_name.errorId} message={fields.display_name.errors?.[0]} />
        </div>

        <div className="space-y-1.5">
          <LabelRow htmlFor={fields.organization.id}>
            {instructor ? "Where do you teach?" : "Where do you train?"}{" "}
            <span className="text-sm font-normal text-muted-foreground">(optional)</span>
          </LabelRow>
          <Input
            {...getInputProps(fields.organization, { type: "text" })}
            placeholder="School, department, or agency"
            autoComplete="organization"
            className="h-12 text-base"
          />
          <FieldError id={fields.organization.errorId} message={fields.organization.errors?.[0]} />
        </div>

        <div className="space-y-1.5">
          <LabelRow htmlFor={fields.phone.id}>
            Phone <span className="text-sm font-normal text-muted-foreground">(optional)</span>
          </LabelRow>
          <Input
            {...getInputProps(fields.phone, { type: "tel" })}
            placeholder="(555) 123-4567"
            autoComplete="tel"
            className="h-12 text-base"
          />
          <FieldError id={fields.phone.errorId} message={fields.phone.errors?.[0]} />
        </div>

        {answers.referral_source === REFERRAL_SOURCE_OTHER ? (
          // Asked here, not on the referral step: free text that may name a
          // person is only collected beside the consent that governs it.
          <div className="space-y-1.5">
            <LabelRow htmlFor={fields.referral_source_other.id}>
              How did you hear about us?{" "}
              <span className="text-sm font-normal text-muted-foreground">(optional)</span>
            </LabelRow>
            <Input
              {...getInputProps(fields.referral_source_other, { type: "text" })}
              placeholder="A specific person, podcast, conference…"
              maxLength={50}
              className="h-12 text-base"
            />
            <FieldError
              id={fields.referral_source_other.errorId}
              message={fields.referral_source_other.errors?.[0]}
            />
          </div>
        ) : null}

        <div className="space-y-2 pt-1">
          <DocumentCheckbox
            field={fields.accept_tos}
            document="tos"
            version={env.tosVersion}
          />
          <DocumentCheckbox
            field={fields.accept_privacy}
            document="privacy"
            version={env.privacyVersion}
          />
        </div>
      </div>

      <StepFooter>
        {/* Sized like the other steps' Continue on a desktop card. */}
        <div className="md:mx-auto md:w-80">
          <SubmitButton label="Finish setup" />
        </div>
      </StepFooter>
    </form>
  );
}
